import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import API from '@/lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCartItems([]);
      setCartCount(0);
      return;
    }
    try {
      const res = await API.get('/cart');
      setCartItems(res.data);
      setCartCount(res.data.reduce((sum, item) => sum + item.quantity, 0));
    } catch {
      // Silently fail - don't crash
    }
  }, [user]);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    await API.post('/cart', { product_id: productId, quantity });
    await fetchCart();
  }, [fetchCart]);

  const updateQuantity = useCallback(async (itemId, quantity) => {
    if (quantity <= 0) {
      await API.delete(`/cart/${itemId}`);
    } else {
      await API.put(`/cart/${itemId}`, { quantity });
    }
    await fetchCart();
  }, [fetchCart]);

  const removeItem = useCallback(async (itemId) => {
    await API.delete(`/cart/${itemId}`);
    await fetchCart();
  }, [fetchCart]);

  const contextValue = useMemo(
    () => ({ cartItems, cartCount, fetchCart, addToCart, updateQuantity, removeItem }),
    [cartItems, cartCount, fetchCart, addToCart, updateQuantity, removeItem],
  );

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
